<template>
  <header class="navbar bg-base-100 border-b border-solid border-base-200 px-4 sm:px-6 lg:px-10 py-3 relative">
    <div class="flex items-center justify-between w-full gap-4">
      <router-link to="/" class="flex items-center gap-3 sm:gap-4 text-base-content">
        <div class="size-10 shrink-0">
          <img :src="logo" width="40px" height="40px" alt="Logo" class="h-full w-full object-contain" />
        </div>
        <h2 class="text-base-content text-base sm:text-lg font-bold leading-tight tracking-[-0.015em] truncate max-w-[180px] sm:max-w-none">
          {{ inmob_data.inmobiliaria_details.nombre }}
        </h2>
      </router-link>

      <button
        class="btn btn-ghost lg:hidden min-h-[44px] min-w-[44px] px-3"
        @click="toggleMobileMenu"
        :aria-expanded="mobileMenuOpen.toString()"
        aria-label="Abrir menú"
      >
        <svg v-if="!mobileMenuOpen" xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
        </svg>
        <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>

      <div class="hidden lg:flex flex-1 justify-end gap-8">
        <div class="flex items-center gap-9">
          <router-link v-if="inmob_data.home_page_content.is_active" to="/" class="text-base-content hover:text-primary text-sm font-medium leading-normal">Inicio</router-link>
          <router-link v-if="inmob_data.search_page_content.is_active" to="/search?propertyType=casa&transactionType=alquiler" class="text-base-content hover:text-primary text-sm font-medium leading-normal">Alquiler</router-link>
          <router-link v-if="inmob_data.search_page_content.is_active" to="/search?propertyType=casa&transactionType=venta" class="text-base-content hover:text-primary text-sm font-medium leading-normal">Compra</router-link>
          <router-link v-if="inmob_data.blog_page_content.is_active" to="/blog" class="text-base-content hover:text-primary text-sm font-medium leading-normal">Blog</router-link>
          <router-link to="/comparison" class="relative text-base-content hover:text-primary text-sm font-medium leading-normal">
            <span>Comparar</span>
            <span v-if="comparisonCount > 0" class="absolute -top-2 -right-2 bg-primary text-primary-content text-xs rounded-full px-1.5 py-0.5 min-w-[1.25rem] text-center">{{ comparisonCount }}</span>
          </router-link>
          <router-link v-if="inmob_data.contact_page_content.is_active" to="/contact" class="text-base-content hover:text-primary text-sm font-medium leading-normal">Contacto</router-link>
        </div>

        <div v-if="!isAuthenticated" class="flex items-center gap-4">
          <router-link to="/login" class="btn btn-ghost min-w-[84px] h-10 px-4 text-sm font-medium leading-normal">Iniciar sesión</router-link>
          <router-link to="/register" class="btn btn-primary min-w-[84px] h-10 px-4 text-sm font-bold leading-normal tracking-[0.015em]">Registrarse</router-link>
        </div>

        <div v-else class="flex items-center">
          <div class="mr-4">
            <router-link to="/account/alerts" class="relative flex items-center justify-center w-10 h-10 rounded-full hover:bg-base-200 transition-colors" title="Alertas y notificaciones">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-base-content" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
              <span v-if="notificationCount" class="absolute top-0 right-0 bg-error text-white text-xs rounded-full px-1.5 py-0.5">{{ notificationCount }}</span>
            </router-link>
          </div>

          <div class="dropdown dropdown-end">
            <div tabindex="0" role="button" class="w-10 h-10 rounded-full bg-primary text-primary flex items-center justify-center font-bold text-lg uppercase ring ring-primary ring-offset-base-100 ring-offset-2 border-primary border border-black select-none" :title="getUser?.name + ' ' + getUser?.lastname">
              {{ getUser?.name?.charAt(0) }}{{ getUser?.lastname?.charAt(0) }}
            </div>
            <ul tabindex="0" class="dropdown-content z-[1] menu p-2 shadow bg-base-100 rounded-box w-52 mt-2">
              <li><router-link to="/account/profile">Perfil</router-link></li>
              <li><router-link to="/account/favorites">Mis favoritos</router-link></li>
              <li><router-link to="/account/alerts">Mis alertas</router-link></li>
              <li><router-link to="/account/mi-rent">Mi alquiler</router-link></li>
              <li><router-link to="/account/mis-propiedades">Mis propiedades</router-link></li>
              <li><button @click="$store.dispatch('auth/logout')">Cerrar sesión</button></li>
            </ul>
          </div>
        </div>
      </div>
    </div>

    <transition name="mobile-menu">
      <div v-if="mobileMenuOpen" class="lg:hidden absolute left-0 right-0 top-full z-50 border-t border-base-200 bg-white shadow-[0_24px_60px_rgba(15,23,42,0.12)]">
        <div class="px-4 sm:px-6 py-4 space-y-4">
          <nav class="space-y-1">
            <router-link v-if="inmob_data.home_page_content.is_active" to="/" class="flex items-center justify-between rounded-xl px-4 py-3 text-base-content font-medium hover:bg-base-200" @click.native="closeMobileMenu">Inicio</router-link>
            <router-link v-if="inmob_data.search_page_content.is_active" to="/search?propertyType=casa&transactionType=alquiler" class="flex items-center justify-between rounded-xl px-4 py-3 text-base-content font-medium hover:bg-base-200" @click.native="closeMobileMenu">Alquiler</router-link>
            <router-link v-if="inmob_data.search_page_content.is_active" to="/search?propertyType=casa&transactionType=venta" class="flex items-center justify-between rounded-xl px-4 py-3 text-base-content font-medium hover:bg-base-200" @click.native="closeMobileMenu">Compra</router-link>
            <router-link v-if="inmob_data.blog_page_content.is_active" to="/blog" class="flex items-center justify-between rounded-xl px-4 py-3 text-base-content font-medium hover:bg-base-200" @click.native="closeMobileMenu">Blog</router-link>
            <router-link to="/comparison" class="flex items-center justify-between rounded-xl px-4 py-3 text-base-content font-medium hover:bg-base-200" @click.native="closeMobileMenu">
              <span class="flex items-center gap-2">Comparar <span v-if="comparisonCount > 0" class="bg-primary text-primary-content text-xs rounded-full px-1.5 py-0.5 min-w-[1.25rem] text-center">{{ comparisonCount }}</span></span>
            </router-link>
            <router-link v-if="inmob_data.contact_page_content.is_active" to="/contact" class="flex items-center justify-between rounded-xl px-4 py-3 text-base-content font-medium hover:bg-base-200" @click.native="closeMobileMenu">Contacto</router-link>
          </nav>

          <div class="pt-2 border-t border-base-200">
            <div v-if="!isAuthenticated" class="grid grid-cols-1 gap-3">
              <router-link to="/login" class="btn btn-ghost w-full min-h-[44px]" @click.native="closeMobileMenu">Iniciar sesión</router-link>
              <router-link to="/register" class="btn btn-primary w-full min-h-[44px]" @click.native="closeMobileMenu">Registrarse</router-link>
            </div>

            <div v-else class="space-y-2">
              <router-link to="/account/profile" class="block rounded-xl px-4 py-3 text-base-content font-medium hover:bg-base-200" @click.native="closeMobileMenu">Perfil</router-link>
              <router-link to="/account/favorites" class="block rounded-xl px-4 py-3 text-base-content font-medium hover:bg-base-200" @click.native="closeMobileMenu">Mis favoritos</router-link>
              <router-link to="/account/alerts" class="block rounded-xl px-4 py-3 text-base-content font-medium hover:bg-base-200" @click.native="closeMobileMenu">Mis alertas</router-link>
              <router-link to="/account/mi-rent" class="block rounded-xl px-4 py-3 text-base-content font-medium hover:bg-base-200" @click.native="closeMobileMenu">Mi alquiler</router-link>
              <router-link to="/account/mis-propiedades" class="block rounded-xl px-4 py-3 text-base-content font-medium hover:bg-base-200" @click.native="closeMobileMenu">Mis propiedades</router-link>
              <button class="btn btn-ghost w-full min-h-[44px]" @click="logoutAndClose">Cerrar sesión</button>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </header>
</template>

<script>
import { mapState, mapGetters } from 'vuex';

export default {
  data() {
    return {
      mobileMenuOpen: false
    }
  },
  computed: {
    ...mapState({
      inmob_data: state => state.inmob_data,
      notifications: state => state.savedSearches.notifications,
      unreadNotifications: state => state.savedSearches.unreadCount,
    }),
    ...mapGetters({
      isAuthenticated: 'auth/isAuthenticated',
      getUser: 'auth/getUser',
      comparisonCount: 'comparison/comparisonCount'
    }),
    logo() {
      return `${process.env.VUE_APP_API_BASE_URL}${this.inmob_data.inmobiliaria_details.logo}`;
    },
    notificationCount() {
      return this.unreadNotifications;
    }
  },
  watch: {
    '$route.fullPath'() {
      this.mobileMenuOpen = false
    }
  },
  mounted() {
    this.setNavbarHeight()
    window.addEventListener('resize', this.handleResize)
    if (this.isAuthenticated) {
      this.$store.dispatch('savedSearches/fetchNotifications').catch(() => {});
    }
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
  },
  methods: {
    setNavbarHeight() {
      this.$nextTick(() => {
        const navbarHeight = this.$el?.offsetHeight || 0
        document.documentElement.style.setProperty('--navbar-height', `${navbarHeight}px`)
      })
    },
    toggleMobileMenu() {
      this.mobileMenuOpen = !this.mobileMenuOpen
    },
    closeMobileMenu() {
      this.mobileMenuOpen = false
    },
    handleResize() {
      if (window.innerWidth >= 1024) {
        this.mobileMenuOpen = false
      }
      this.setNavbarHeight()
    },
    logoutAndClose() {
      this.closeMobileMenu()
      this.$store.dispatch('auth/logout')
    }
  }
};
</script>

<style scoped>
.mobile-menu-enter-active,
.mobile-menu-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.mobile-menu-enter,
.mobile-menu-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}
</style>
